import React, { FC, useEffect, useState } from 'react';

import { QuestionForm, SignificantLevel } from '../../../models/questionModel';
import useQuestionStore, { FormType } from '../../../store/QuestionStore';
import { ReportHandler } from '../../ReportPage/Report';
import styles from '../RiskTransfer.module.scss';

interface FormDetails {
  title: string;
  details: string[];
  type: FormType;
  significance: SignificantLevel | null;
  marks: number;
}

const RiskAssesmentSummaryPage: FC<ReportHandler> = ({ validChanged }) => {
  const { getQuestionByForm } = useQuestionStore();

  const [details, setDetails] = useState<FormDetails[]>([
    {
      title: 'Liquidation Ratio',
      type: 'liquidation',
      details: [
        'Prepare detailed cash flow forecasts (short-, medium-, long-term)',
        'Maintain liquidity buffers (cash, marketable securities)',
        'Secure committed credit lines',
        'Conduct stress testing and scenario analysis',
        'Implement contingency funding plans',
      ],
      significance: null,
      marks: 0,
    },
    {
      title: 'Funding Risk',
      type: 'funding',
      details: [
        'Diversify funding sources (banks, markets, equity, trade finance) ',
        'Extend debt maturity profile and reduce short-term reliance ',
        'Secure committed facilities for emergencies ',
        'Maintain strong creditworthiness ',
        'Monitor covenants and regulatory requirements',
      ],
      significance: null,
      marks: 0,
    },
    {
      title: 'Credit Risk',
      type: 'credit',
      details: [
        'Conduct ALM gap analysis and duration matching ',
        'Use derivatives (swaps, futures, options) to hedge ',
        'Diversify between fixed- and floating-rate instruments ',
        'Stagger refinancing schedules ',
        'Monitor sensitivity with duration/convexity analysis',
      ],
      significance: null,
      marks: 0,
    },
    {
      title: 'Commodity Risk',
      type: 'commodity',
      details: [
        'Hedge exposures with forwards, swaps, or options ',
        'Match revenues and costs in the same currency (natural hedge) ',
        'Net receivables/payables across currencies ',
        'Diversify geographic markets and sourcing ',
        'Use centralized treasury and compliance with FX regulations',
      ],
      significance: null,
      marks: 0,
    },
    {
      title: 'Currency Risk',
      type: 'currency',
      details: [
        'Use commodity derivatives (futures, swaps, options) ',
        'Lock in long-term supply/sales contracts ',
        'Diversify suppliers and build inventory reserves ',
        'Monitor hedge effectiveness and basis risk ',
        'Maintain liquidity to cover margin calls',
      ],
      significance: null,
      marks: 0,
    },
    {
      title: 'Interest Rate Risk',
      type: 'interest',
      details: [
        'Perform credit assessments and scoring  ',
        'Diversify credit portfolio across sectors and borrowers ',
        'Set exposure and concentration limits ',
        'Secure collateral and revalue regularly ',
        'Monitor counterparties and conduct stress testing',
      ],
      significance: null,
      marks: 0,
    },
    {
      title: 'Operation Risk',
      type: 'operational',
      details: [
        'Standardize and document processes ',
        'Train staff and enforce segregation of duties ',
        'Strengthen IT/cybersecurity, backups, and DRP/BCP ',
        'Conduct vendor due diligence and monitoring ',
        'Establish whistleblowing and fraud detection controls',
      ],
      significance: null,
      marks: 0,
    },
    {
      title: 'Disaster Risk',
      type: 'disaster',
      details: [
        'Build financial buffers and flexible budgeting  ',
        'Diversify revenue streams and supply chains  ',
        'Hedge against inflation and currency shocks ',
        'Conduct stress testing and recovery planning ',
        'Engage regulators and stakeholders proactively',
      ],
      significance: null,
      marks: 0,
    },
    {
      title: 'Compliance Risk',
      type: 'compliance',
      details: [
        'Use standardized, reviewed contracts with clear dispute clauses ',
        'Conduct due diligence on counterparties ',
        'Protect intellectual property (trademarks, copyrights, patents) ',
        'Maintain HR and labor law compliance ',
        'Use ADR to manage disputes and secure liability insurance',
      ],
      significance: null,
      marks: 0,
    },
    {
      title: 'Legal Risk',
      type: 'legal',
      details: [
        'Maintain updated compliance registers and policies ',
        'Provide regular staff training and awareness ',
        'Conduct internal/external compliance audits ',
        'Monitor vendors for regulatory adherence ',
        'Establish whistleblowing channels and crisis communication plans',
      ],
      significance: null,
      marks: 0,
    },
  ]);

  const getBackgroundColor = (color: 'green' | 'yellow' | 'orange' | 'red') => {
    switch (color) {
      case 'green':
        return 'limegreen';
      case 'yellow':
      case 'orange':
        return color;
      case 'red':
        return 'rgba(255,0,0,0.6)';
    }
  };

  useEffect(() => {
    setDetails((prev: FormDetails[]) => {
      const newDetails = prev.map((p) => {
        const question = getQuestionByForm(p.type);
        const marks = question.getTotalMarks();
        return { ...p, significance: question.isSignificant, marks: marks };
      });

      return newDetails;
    });

    validChanged(true);
  }, []);

  return (
    <div>
      <div>Summary of all forms</div>
      <ol>
        {details.map((det) => {
          if (det.significance) {
            return (
              <>
                <li key={det.title}>
                  {det.title} (
                  <span
                    style={{
                      backgroundColor: getBackgroundColor(
                        det.significance.color
                      ),
                      padding: '3px 5px',
                    }}
                  >
                    {det.significance.isSignificant
                      ? 'Significant'
                      : 'Not Significant'}
                  </span>
                  <span> - {det.marks}</span>)
                </li>
                {det.significance.isSignificant && (
                  <ul>
                    {det.details.map((d) => (
                      <li key={d}>{d}</li>
                    ))}
                  </ul>
                )}
              </>
            );
          } else {
            return <li key={det.title}>{det.title} (Not Applicable)</li>;
          }
        })}
      </ol>
    </div>
  );
};

export default RiskAssesmentSummaryPage;
